<script lang="ts">
	import { getTranslate } from "@utils/translate.js";
	import type { Application } from "@core/schema/Application.js";
	import type { Competition } from "@core/schema/Competition.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { calculateApplicationTotalPrice, getPaymentQrStrings } from "@core/utils/paymentQr.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./PaymentQrCodes.translations.json" with { type: "json" };

	const {
		competition,
		applications,
	}: {
		competition: Competition;
		applications: { application: Application; label?: string }[];
	} = $props();

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	async function generateQrDataUrl(text: string): Promise<string> {
		const QRCode = await import("qrcode");
		return QRCode.default.toDataURL(text, { margin: 1, width: 200 });
	}

	const qrItems = $derived(
		applications
			.map(({ application, label }) => {
				const totalCzk = calculateApplicationTotalPrice(application);
				const qrStrings = getPaymentQrStrings(competition, totalCzk, application.variableSymbol);
				return {
					id: application.id,
					variableSymbol: application.variableSymbol,
					label,
					totalCzk,
					czechPromise: qrStrings.czech !== null ? generateQrDataUrl(qrStrings.czech) : null,
					intlPromise: qrStrings.international !== null ? generateQrDataUrl(qrStrings.international) : null,
				};
			})
			.filter((item) => item.czechPromise !== null || item.intlPromise !== null),
	);
</script>

{#if qrItems.length > 0}
	<div class="border-ink-200 flex flex-col gap-4 rounded-xl border bg-white p-4 sm:p-5">
		<div class="flex flex-col gap-1.5">
			<p class="font-display text-ink-950 text-[1.3rem] font-bold uppercase leading-tight tracking-[-0.01em]">
				{translate("payment")}
			</p>
			<p class="text-ink-600 text-sm leading-relaxed">{translate("paymentNote")}</p>
		</div>

		{#if competition.payment?.bankAccountNumber}
			<div class="bg-ink-50 flex flex-wrap gap-x-6 gap-y-1 rounded-lg px-4 py-3 text-sm">
				<span class="text-ink-600">
					{translate("accountNumber")}: <b class="tabular text-ink-900">{competition.payment.bankAccountNumber}</b>
				</span>
				{#if competition.payment.constantSymbol}
					<span class="text-ink-600">
						{translate("constantSymbol")}: <b class="tabular text-ink-900">{competition.payment.constantSymbol}</b>
					</span>
				{/if}
			</div>
		{/if}

		{#if qrItems.length > 1}
			<p class="text-ink-500 text-xs">{translate("scanQrCode")}</p>
		{/if}

		{#each qrItems as item (item.id)}
			<div class="border-ink-100 flex flex-col gap-3 border-t pt-4 first:border-t-0 first:pt-0">
				<div class="flex flex-col gap-3">
					<div class="flex flex-col sm:flex-row gap-4 items-start">
						{#if item.czechPromise}
							<div class="border-ink-200 flex flex-1 flex-col items-center gap-2 rounded-xl border p-4">
								<p class="eyebrow text-ink-700 font-semibold">{translate("czechQr")}</p>
								{#await item.czechPromise}
									<div class="bg-ink-100 h-[200px] w-[200px] animate-pulse rounded-lg"></div>
								{:then dataUrl}
									<img src={dataUrl} alt={translate("czechQr")} width="200" height="200" class="rounded-lg" />
								{/await}
							</div>
						{/if}

						{#if item.intlPromise}
							<div class="border-ink-200 flex flex-1 flex-col items-center gap-2 rounded-xl border p-4">
								<p class="eyebrow text-ink-700 font-semibold">{translate("internationalQr")}</p>
								{#await item.intlPromise}
									<div class="bg-ink-100 h-[200px] w-[200px] animate-pulse rounded-lg"></div>
								{:then dataUrl}
									<img src={dataUrl} alt={translate("internationalQr")} width="200" height="200" class="rounded-lg" />
								{/await}
							</div>
						{/if}
					</div>

					<div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
						{#if item.label}<span class="text-ink-900 font-semibold">{item.label}</span>{/if}
						<span class="tabular text-secondary text-lg font-bold">
							{formatPrice(item.totalCzk, tenant.locale, false)}
						</span>
					</div>

					{#if item.variableSymbol}
						<p class="text-ink-600 text-sm">
							{translate("variableSymbol")}: <b class="tabular text-ink-900">{item.variableSymbol}</b>
						</p>
					{/if}
				</div>
			</div>
		{/each}
	</div>
{/if}
